/**
 * O gesto, a supressão que ele exige e a leitura do log de diagnóstico.
 *
 * ── Por que num arquivo separado do painel ──────────────────────────────────
 *
 * `react-refresh/only-export-components` é **erro** neste projeto, e ela está
 * certa: um arquivo que exporta componente E hook quebra o fast refresh, então
 * editar o painel durante o desenvolvimento perderia o estado da tela. Hooks
 * aqui, componente no `.tsx`.
 */

import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import type { CSSProperties } from "react";
import {
  formatBootLog,
  getBootLog,
  subscribeBootLog,
  type BootEntry,
} from "../../lib/boot/bootTrace";

/** Quanto tempo de toque abre o painel. Longo o bastante pra não sair sem querer. */
export const LONG_PRESS_MS = 5000;

/**
 * O que o iOS faz durante 5 segundos de dedo parado, e que precisa ser
 * desligado onde o gesto vive.
 *
 * `user-select: none` mata a **lupa de seleção** e o realce azul de texto.
 * `-webkit-touch-callout: none` mata o **menu de contexto** (Copiar / Procurar /
 * Compartilhar) — que é outro mecanismo, e é ele que rouba o gesto quando o
 * dedo cai sobre texto. Um sem o outro deixa metade do problema de pé, e é por
 * isso que os dois viajam juntos.
 *
 * ⚠️ `WebkitTouchCallout` **não tem utility no Tailwind** — daí ser `style` e
 * não classe. O `select-none` existe, mas separar as duas metades em dois
 * mecanismos (uma classe e um style) é como uma delas se perde num refactor.
 */
export const NO_LONG_PRESS_INTERFERENCE: CSSProperties = {
  WebkitUserSelect: "none",
  userSelect: "none",
  WebkitTouchCallout: "none",
};

export function useDiagnosticLog(): readonly BootEntry[] {
  return useSyncExternalStore(subscribeBootLog, getBootLog, getBootLog);
}

/**
 * Copia o log formatado. Devolve `false` quando não deu — nunca lança.
 *
 * ── Por que é um dono único e não um `writeText` em cada call site ───────────
 *
 * Nasceu privada no painel de diagnóstico e passou a ser precisa também pelo
 * botão "Copiar diagnóstico" do card de erro da vitrine. Duas transcrições da
 * mesma coisa é a doença do BUG-81/BUG-87: divergiriam no dia em que o formato
 * do log mudasse, e o sintoma seria "o botão de um lugar copia menos que o do
 * outro" — invisível até alguém comparar dois prints.
 *
 * ⚠️ O `catch` não é decorativo: o WKWebView **nega a área de transferência**
 * fora de gesto do usuário em alguns estados, e `navigator.clipboard` não
 * existe em contexto não-seguro. Quem chama tem de tratar o `false` — o painel
 * ainda mostra o texto na tela (o print resolve), mas o card de erro NÃO
 * mostra, e ali falhar em silêncio deixaria a pessoa achando que copiou.
 */
export async function copyDiagnosticText(): Promise<boolean> {
  const texto = formatBootLog();
  if (!texto) return false;
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    return false;
  }
}

/**
 * Gesto de toque longo. `pointer*` cobre toque e mouse com um handler só; o
 * cancelamento precisa incluir `move` porque rolar a tela com o dedo parado
 * não pode contar como pressão contínua.
 *
 * ⚠️ O `style` de supressão sai DAQUI, junto dos handlers, e não é escolha de
 * estilo: enquanto ele morava no call site, aplicar o gesto sem a supressão era
 * possível — e o `BootScreen` era o único que lembrava, então o card da vitrine
 * nasceu com o gesto competindo contra o menu de contexto do iOS. Vindo do
 * hook, esquecer passa a ser **inexpressável**.
 *
 * Quem espalhar isto e escrever `style` próprio depois SOBRESCREVE a supressão.
 * É a única forma de errar que sobrou, e há teste guardando os dois call sites.
 */
export function useLongPress(onTrigger: () => void) {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cancel = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = undefined;
  }, []);

  const start = useCallback(() => {
    cancel();
    timer.current = setTimeout(onTrigger, LONG_PRESS_MS);
  }, [cancel, onTrigger]);

  useEffect(() => cancel, [cancel]);

  return {
    onPointerDown: start,
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onPointerLeave: cancel,
    onPointerMove: cancel,
    style: NO_LONG_PRESS_INTERFERENCE,
  };
}
